/**
 * src/lib/tutor-diagram.ts
 *
 * Parse + validate a tutor-supplied diagram spec into a LessonDiagram
 * the existing <Diagram /> component can render. The tutor model writes
 * diagram specs as fenced code blocks:
 *
 *     ```diagram
 *     {
 *       "type": "function-graph",
 *       "params": { "kind": "quadratic", "a": 1, "b": 0, "c": 0 },
 *       "caption": "y = x²"
 *     }
 *     ```
 *
 * The renderer (TutorChatPanel) detects the language=diagram code block,
 * runs this parser, and either renders the diagram or falls back to a
 * normal code block on parse failure.
 *
 * Why a separate parser:
 *   - Pure function so we can unit-test the (model-written) JSON
 *     handling without mounting React.
 *   - Centralizes the type allowlist so a typo in the model's "type"
 *     value fails closed (renders as code) instead of crashing the
 *     diagram dispatcher.
 */
import type { LessonDiagram } from '@/lib/study-plan/lesson-generation'

/** The diagram types we render — must match Diagram.tsx's switch.
 *  Was 6 in v7 (initial diagram support); v9 added right-triangle +
 *  unit-circle (trig/geometry); v10 added rectangular-prism + cylinder;
 *  v11 added circle (general circle geometry); v12 added triangle +
 *  parallel-lines + polygon (the Lines/Angles figure set); v13 added
 *  cone + sphere + pyramid — whose volume formulas (V = (1/3)πr²h,
 *  (4/3)πr³, (1/3)ℓwh) ARE on the SAT reference sheet (an earlier
 *  comment wrongly claimed they weren't); v14 added box-plot + dot-plot
 *  for One-Variable Data distributions; v15 added right-triangle-altitude
 *  (altitude to the hypotenuse — the geometric-mean figure, with A/B/C/D
 *  vertex labels the plain right-triangle can't do). */
export const TUTOR_DIAGRAM_TYPES = [
  'function-graph',
  'number-line',
  'sentence-diagram',
  'comparison-table',
  'decision-tree',
  'highlighted-passage',
  'right-triangle',
  'right-triangle-altitude',
  'unit-circle',
  'rectangular-prism',
  'cylinder',
  'circle',
  'triangle',
  'parallel-lines',
  'polygon',
  'cone',
  'sphere',
  'pyramid',
  'box-plot',
  'dot-plot',
  'shape',
] as const satisfies ReadonlyArray<LessonDiagram['type']>

export type TutorDiagramType = (typeof TUTOR_DIAGRAM_TYPES)[number]

export type ParseResult = { ok: true; diagram: LessonDiagram } | { ok: false; error: string }

/**
 * Parse a JSON string into a LessonDiagram. Tolerates surrounding
 * whitespace; rejects malformed JSON, missing/invalid type, or
 * non-object params.
 */
export function parseTutorDiagramSpec(raw: string): ParseResult {
  const trimmed = raw.trim()
  if (!trimmed) return { ok: false, error: 'empty diagram spec' }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (err) {
    return {
      ok: false,
      error: `invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'diagram spec must be a JSON object' }
  }
  const obj = parsed as Record<string, unknown>

  // Type must be one of the supported variants. The model could
  // misspell ("functiongraph", "number_line"); fail closed.
  const type = obj.type
  if (typeof type !== 'string') {
    return { ok: false, error: 'missing or non-string `type`' }
  }
  if (!(TUTOR_DIAGRAM_TYPES as ReadonlyArray<string>).includes(type)) {
    return {
      ok: false,
      error: `unsupported diagram type "${type}"; must be one of ${TUTOR_DIAGRAM_TYPES.join(', ')}`,
    }
  }

  // Params: must be an object (each diagram component does its own
  // per-field validation, so we don't second-guess shape here).
  const params = obj.params
  if (
    params === undefined ||
    params === null ||
    typeof params !== 'object' ||
    Array.isArray(params)
  ) {
    return { ok: false, error: 'missing or non-object `params`' }
  }

  // Optional caption.
  const caption = typeof obj.caption === 'string' ? obj.caption : undefined

  // The `id` field is required by LessonDiagram for the lesson use-case
  // (it's the lookup key for `[[diagram:<id>]]` markers). Tutor diagrams
  // don't go through that lookup path — they're inlined — so we mint a
  // synthetic id. Doesn't matter what it is; just satisfies the type.
  const id = typeof obj.id === 'string' ? obj.id : 'tutor-inline'

  return {
    ok: true,
    diagram: {
      id,
      type: type as TutorDiagramType,
      params: params as Record<string, unknown>,
      caption,
    },
  }
}

const DIAGRAM_BLOCK_RE = /```diagram\n([\s\S]*?)```/

/** Raw JSON body of the first ```diagram block in a stem, or null if none. */
export function extractDiagramBlock(stem: string): string | null {
  const m = (stem || '').match(DIAGRAM_BLOCK_RE)
  return m ? m[1].trim() : null
}

/** True if the stem embeds a ```diagram block. */
export function hasDiagramBlock(stem: string): boolean {
  return extractDiagramBlock(stem) !== null
}

/**
 * Repair a stem whose ```diagram fences were stripped on export. If the stem
 * has a bare `diagram\n{...}` block whose body parses as a valid diagram spec,
 * re-wrap it. Idempotent + conservative — mirrors normalizeChartFences.
 */
export function normalizeDiagramFences(stem: string): string {
  const s = stem || ''
  if (s.includes('```diagram')) return s
  return s.replace(/\n+[ \t]*diagram[ \t]*\n(\{[\s\S]*\})\n+/, (full, body: string) =>
    parseTutorDiagramSpec(body).ok ? `\n\n\`\`\`diagram\n${body.trim()}\n\`\`\`\n\n` : full,
  )
}

/* ------------------------------------------------------------------ */
/*  Shared prompt instruction blocks                                   */
/*                                                                     */
/*  Single source of truth for HOW models are told to write math and   */
/*  diagrams — used by BOTH the standalone /tutor prompt               */
/*  (tutor-context.ts) and the practice/review TutorChat prompt        */
/*  (tutor.ts). Living next to the spec parser keeps the instructions  */
/*  and the renderer in lockstep. Text moved VERBATIM from             */
/*  tutor-context.ts (same template-literal escapes).                  */
/* ------------------------------------------------------------------ */

export const MATH_NOTATION_INSTRUCTIONS = `MATH NOTATION:
Your responses render KaTeX math via $ ... $ for inline and $$ ... $$ for
block. USE IT for any expression with fractions, radicals, exponents,
subscripts, integrals, sums, or matrices — these look ugly as plain text and
beautiful in KaTeX. Examples:

  - Inline:  the slope is $m = \\frac{y_2 - y_1}{x_2 - x_1}$
  - Block:   $$\\sin 30° = \\frac{\\text{opposite}}{\\text{hypotenuse}} = \\frac{1}{2}$$
  - Bad:     "the slope is m = (y2 - y1) / (x2 - x1)"   ← unicode + slashes; ugly
  - Bad:     "sin 30° = opp/hyp = 1/2"                  ← reads as code, not math

Skip KaTeX for trivial single-symbol references (just write \`x = 5\` in
backticks). Use it whenever the expression has any STRUCTURE — stacks,
indices, special operators.

EQUATION STEPS — ONE PER LINE:
When you walk through algebra, put EVERY step on its own line — never chain
steps in one paragraph. Use block math with one equation per line (or a
short numbered list). The student must see exactly where one equation ends
and the next begins. Example:

$$15x - 4500 > 6000$$

$$15x > 10500$$

$$x > 700$$

  - Bad: "15x - 4500 > 6000 so 15x > 10500 so x > 700" — all in one line.`

export const REFERENCE_ANSWER_INSTRUCTIONS = `REFERENCE / CHEAT-SHEET QUESTIONS — the ONE exception to your usual brevity:
When the student asks you to LIST or ENUMERATE a set of facts — "what are the
values of...", "give me all the...", "the formulas for...", a summary table, a
cheat sheet, special-angle trig values, common conversions, a rules list — a
terse reply is the WRONG reply. These deserve a complete, well-organized
reference. For these (and ONLY these):
  - Drop your usual length limit. Be as long as completeness genuinely needs
    (but no padding — every row/line earns its place).
  - LEAD with a markdown TABLE of the values — that table IS the artifact the
    student came for.
  - Section it with ## headings (e.g. Table · Diagram · Formulas · How to remember).
  - Add ONE relevant diagram if a supported type fits (unit-circle for
    special-angle trig, function-graph for a family of functions, etc.).
  - Include a mnemonic / memory trick whenever a well-known one exists.
Do NOT trigger this for coaching or "why did I miss this" questions — those
stay tight per your normal length rules.

Canonical example — "sin/cos/tan/cot of the special angles" should return: a
full markdown table (columns Angle · Radians · sin · cos · tan · cot, rows
0°/30°/45°/60°/90°), a \`\`\`diagram unit-circle block, the identities
$\\tan\\theta = \\frac{\\sin\\theta}{\\cos\\theta}$ and
$\\cot\\theta = \\frac{\\cos\\theta}{\\sin\\theta}$ in KaTeX, and the sine memory
pattern ($\\sin$ of 0/30/45/60/90 $= \\frac{\\sqrt0}{2}, \\frac{\\sqrt1}{2},
\\frac{\\sqrt2}{2}, \\frac{\\sqrt3}{2}, \\frac{\\sqrt4}{2}$; cosine is that list
reversed).`

export const DIAGRAM_INSTRUCTIONS = `DIAGRAMS:
You can render real SVG diagrams inline by writing a fenced code block
with language "diagram" and a JSON spec inside. The renderer dispatches
to one of twenty-one types. Use a diagram when a picture meaningfully outperforms
words — graphing a function, showing an inequality on a number line, comparing
two ideas in a table. Don't overuse: a single diagram per response max,
and only when it changes understanding (not just decorates).

LABEL WHAT YOU NAME: if your explanation refers to specific points, vertices,
or segments by letter (A, B, C, the altitude CD, angle x°, …), the figure MUST
label those exact points — use the type that supports it (triangle's
vertexLabels, right-triangle-altitude's A/B/C/D, shape's per-vertex labels). A
figure whose vertices don't match the letters in your prose is worse than no
figure — it makes the student hunt for points that aren't drawn. If NO supported
type can place a point your prose needs, don't draw a stand-in figure with the
labels crammed onto the wrong sides — describe it in words + KaTeX instead.

Syntax (all twenty-one types):

\`\`\`diagram
{ "type": "function-graph",
  "params": { "kind": "linear", "slope": 2, "intercept": -1, "xMin": -3, "xMax": 5 },
  "caption": "y = 2x - 1" }
\`\`\`

  Types + key params (every param is optional unless noted; sane defaults apply):

  - function-graph:   kind — one of:
                        "linear"      y = slope·x + intercept
                        "quadratic"   y = a·x² + b·x + c
                        "exponential" y = a·base^x + c   (base default 2)
                        "cubic"       y = a·x³ + b·x² + c·x + d
                        "absolute"    y = a·|x − h| + k
                      xMin, xMax, yMin, yMax, highlightX, label,
                      curves: [{ kind, ...params, color? }] — overlay MORE
                        functions on the same axes. USE for a SYSTEM of equations
                        (two lines) — put the second line here and mark the
                        solution with points: [{ x, y, label }].
                      shade: "above" | "below" — shade the region relative to
                        the primary curve. USE for a linear INEQUALITY
                        (y ≥ … → shade "above"; y ≤ … → "below").
                      points: [{ x, y, label? }] — mark multiple points,
                      showRiseRun: boolean — when true with 2+ points on a
                      linear function, draws a dashed right-triangle showing
                      the rise/run between points[0] and points[1] with
                      labels. USE THIS when explaining slope — a plain line
                      with no points marked teaches nothing. Example for
                      slope between (1,3) and (5,11):
                        { "kind": "linear", "slope": 2, "intercept": 1,
                          "xMin": 0, "xMax": 6, "yMin": 0, "yMax": 13,
                          "points": [{ "x": 1, "y": 3, "label": "(1, 3)" },
                                     { "x": 5, "y": 11, "label": "(5, 11)" }],
                          "showRiseRun": true }
  - number-line:      min, max, marks: number[], interval: { from, to, open? }, label
  - comparison-table: title, headers: string[], rows: string[][]
  - sentence-diagram: segments: [{ text, kind?: "independent"|"nonrestrictive"|"highlight"|"punctuation"|"subject"|"predicate"|"fragment" }],
                      title, label
  - decision-tree:    root: { question?, yes?, no?, answer? } (recursive), title, label
  - highlighted-passage: text, highlights: [{ phrase, color: "cyan"|"amber"|"emerald"|"rose", label? }], title, label
  - right-triangle:   base (number, default 3), height (number, default 4) — used for SHAPE only.
                      Labels are arbitrary text: baseLabel ("√3"), heightLabel ("1"), hypLabel ("2"),
                      topAngle ("60°"), bottomRightAngle ("30°"), showRightAngle (default true),
                      label. Right angle is always at bottom-left.
                      Numeric base/height set proportions; pass base=1.732 + baseLabel="√3"
                      to get a visually-correct shape with a math-correct label.

                      ANGLE ↔ SIDE CORRESPONDENCE (CRITICAL — get this wrong and the
                      diagram will mislabel the angles):
                      The angle at a vertex is OPPOSITE the side that does NOT touch
                      that vertex.
                        - topAngle is at the top vertex (touches height + hypotenuse).
                          It is OPPOSITE the BASE.
                        - bottomRightAngle is at the bottom-right vertex (touches
                          base + hypotenuse). It is OPPOSITE the HEIGHT.
                        - bottom-left vertex is the right angle (always 90°).

                      Worked examples — pin these in your head before writing the spec:
                        30-60-90 with base=√3 (≈1.732), height=1, hypotenuse=2:
                          → topAngle="60°" (opposite base √3 → larger of the acute angles)
                          → bottomRightAngle="30°" (opposite height 1 → smaller acute angle)
                        30-60-90 with base=1, height=√3 (≈1.732), hypotenuse=2:
                          → topAngle="30°" (opposite base 1)
                          → bottomRightAngle="60°" (opposite height √3)
                        45-45-90 with base=1, height=1, hypotenuse=√2:
                          → topAngle="45°", bottomRightAngle="45°" (symmetric)
                        3-4-5 with base=3, height=4, hypotenuse=5:
                          → topAngle≈"53°" (opposite the longer leg, 4)
                          → bottomRightAngle≈"37°" (opposite the shorter leg, 3)

                      Sanity check before you submit: the LARGER acute angle must be
                      OPPOSITE the LONGER leg. If you wrote topAngle="30°" but
                      base > height, you swapped them.
  - right-triangle-altitude: A right triangle with the ALTITUDE drawn from the
                      right-angle vertex to the hypotenuse — the geometric-mean /
                      "altitude on the hypotenuse" figure (△ACD ~ △CBD ~ △ABC).
                      This is the ONLY type that labels the four points A, B, C, D
                      and draws the internal altitude segment CD. Right angle is at
                      the APEX C; hypotenuse AB is horizontal at the bottom; D is
                      the foot of the altitude on AB. Params:
                        ad, db (numbers — the two hypotenuse segments AD and DB;
                          these DRIVE the shape. The apex sits at height √(ad·db)
                          so the right angle at C is exact for any values),
                        adLabel, dbLabel ("8", "18" — the segment lengths),
                        cdLabel ("12" — the altitude length),
                        acLabel, bcLabel (the legs, optional),
                        aLabel/bLabel/cLabel/dLabel (vertex labels, default A/B/C/D).
                      USE for altitude-to-hypotenuse, geometric-mean relationships
                      ($CD^2 = AD \\cdot DB$, $AC^2 = AD \\cdot AB$), and the
                      similar-triangles-inside-a-right-triangle setup. Example
                      (AD=8, DB=18, so CD=12, AB=26):
                        { "type": "right-triangle-altitude",
                          "params": { "ad": 8, "db": 18, "adLabel": "8",
                            "dbLabel": "18", "cdLabel": "12" } }
  - unit-circle:      angleDeg (number, the angle to draw, e.g. 30, 45, 60, 90, 120),
                      showSinCos (boolean — draws cos and sin perpendiculars + labels),
                      pointLabel (text shown next to the point, e.g. "(√3/2, 1/2)"),
                      marks (number[] of axis angles to tick, e.g. [0, 90, 180, 270]),
                      label.
  - rectangular-prism: length, width, height (numbers — proportional, not pixel
                      values). Labels: lengthLabel ("ℓ" or "5" or "5 cm"),
                      widthLabel, heightLabel. showHiddenEdges (boolean, default
                      true — dashed back edges). USE for Volume of rectangular
                      prism / box questions (V = ℓwh, which IS on the SAT
                      reference sheet). Example:
                        { "type": "rectangular-prism",
                          "params": { "length": 5, "width": 3, "height": 4,
                                       "lengthLabel": "5", "widthLabel": "3",
                                       "heightLabel": "4" } }
  - cylinder:         radius, height (proportional). Labels: radiusLabel ("r"
                      or "3"), heightLabel. showHiddenEdges (boolean, default
                      true — dashed back-half of bottom ellipse). USE for
                      Volume of cylinder questions (V = πr²h, on the SAT
                      reference sheet). Example:
                        { "type": "cylinder",
                          "params": { "radius": 2, "height": 5,
                                       "radiusLabel": "2", "heightLabel": "5" } }
  - circle:           General circle geometry (NOT trig — use unit-circle for that).
                      Angles are degrees, 0°=east, CCW. All params optional:
                        radiusLabel ("r"/"6") + radiusAngle (default 90),
                        diameterLabel (draws a horizontal diameter),
                        centerLabel (default "O"), showCenter,
                        points: [{ angleDeg, label }] (labeled points A,B,C…),
                        chords: [{ from, to, label }],
                        centralAngle: { from, to, label ("120°"), shade } (two
                          radii + arc; shade fills the sector),
                        inscribedAngle: { vertex, from, to, label },
                        arc: { from, to, label } (highlighted CCW arc),
                        tangentAt: angleDeg (tangent line at that point),
                        withSquare: "circumscribed" | "inscribed" + squareLabel —
                          draw a SQUARE with the circle. "circumscribed" = square
                          AROUND the circle, tangent on all 4 sides (a circle
                          INSCRIBED in a square; side = diameter). "inscribed" =
                          square INSIDE the circle, corners on it (a square
                          inscribed in a circle; diagonal = diameter). USE THIS for
                          any inscribed circle↔square figure — a bare circle
                          without the square misrepresents the problem.
                      USE for circumference/area, arc length, sector area,
                      central vs inscribed angles, chords, tangents, and inscribed
                      circle↔square figures. Example
                      (central angle subtending an arc):
                        { "type": "circle",
                          "params": { "radiusLabel": "6", "centralAngle":
                            { "from": 30, "to": 150, "label": "120°", "shade": true },
                            "points": [ { "angleDeg": 30, "label": "A" },
                                        { "angleDeg": 150, "label": "B" } ] } }
  - triangle:         GENERAL triangle (any angles — acute/obtuse/scalene/isosceles;
                      use right-triangle only for right triangles). Built from its
                      three interior angles. Params: angles [A,B,C] (degrees,
                      sum 180), vertexLabels ["A","B","C"], sideLabels (side
                      OPPOSITE each vertex) ["a","b","c"], angleLabels (override
                      text, e.g. ["x°","","2x°"]), showAngleArcs (default true).
                      USE for angle-sum, exterior-angle, isosceles, similar-triangle
                      questions. Example:
                        { "type": "triangle",
                          "params": { "angles": [40, 75, 65],
                                       "angleLabels": ["40°","75°","x°"] } }
  - parallel-lines:   Two parallel lines cut by a transversal (corresponding,
                      alternate, co-interior angles), OR two intersecting lines
                      (parallel: false → vertical/supplementary angles). Params:
                      parallel (default true), lineLabels { top, bottom },
                      transversalLabel, angles: [{ at: "top"|"bottom",
                      pos: "TL"|"TR"|"BL"|"BR", label }]. Example:
                        { "type": "parallel-lines",
                          "params": { "lineLabels": { "top": "m", "bottom": "n" },
                            "angles": [ { "at": "top", "pos": "TR", "label": "x°" },
                                        { "at": "bottom", "pos": "BL", "label": "y°" } ] } }

                      ANGLE RELATIONSHIPS (get these RIGHT — the #1 source of
                      wrong answer keys). The transversal is drawn from the
                      UPPER-LEFT to the LOWER-RIGHT, so at EACH intersection the
                      acute angles are TL & BR and the obtuse angles are TR & BL.
                      Two angles are EQUAL iff they are in the same class (both
                      acute or both obtuse); otherwise they are SUPPLEMENTARY
                      (sum to 180°). Concretely:
                        EQUAL pairs:
                          - vertical (same intersection): TL=BR, TR=BL
                          - corresponding (same pos, diff intersection):
                            top-TL=bottom-TL, top-TR=bottom-TR, etc.
                          - alternate interior: top-BL=bottom-TR, top-BR=bottom-TL
                          - alternate exterior: top-TL=bottom-BR, top-TR=bottom-BL
                        SUPPLEMENTARY pairs (sum 180°):
                          - adjacent (share a side, same intersection): e.g.
                            TL+TR, TL+BL, TR+BR, BL+BR
                          - co-interior / same-side interior:
                            top-BL+bottom-TL=180, top-BR+bottom-TR=180
                          - same-side exterior: top-TL+bottom-BL=180,
                            top-TR+bottom-BR=180
                      When writing a question, classify the two labeled angles
                      from THIS list and set the answer accordingly. NEVER assume
                      two angles are equal because they "look" similar — co-interior
                      (same-side) angles are SUPPLEMENTARY, not equal.
  - polygon:          Regular polygon (3–12 sides) for interior-angle-sum /
                      regular-polygon questions. Params: sides (3–12), sideLabel,
                      angleLabel ("x°", labels one interior angle), vertexLabels.
                      Example: { "type": "polygon", "params": { "sides": 6, "angleLabel": "x°" } }
  - cone:             Right circular cone, V = (1/3)πr²h (on the SAT reference
                      sheet). Params: radius, height (proportional), radiusLabel,
                      heightLabel, slantLabel, showHiddenEdges. Example:
                        { "type": "cone", "params": { "radius": 3, "height": 4,
                          "radiusLabel": "3", "heightLabel": "4" } }
  - sphere:           Sphere, V = (4/3)πr³ (on the SAT reference sheet). Params:
                      radiusLabel OR diameterLabel, label. Example:
                        { "type": "sphere", "params": { "radiusLabel": "r" } }
  - pyramid:          Right square pyramid, V = (1/3)·base²·h (the (1/3)ℓwh form
                      is on the SAT reference sheet). Params: base (side length),
                      height (proportional), baseLabel, heightLabel,
                      showHiddenEdges. Example:
                        { "type": "pyramid", "params": { "base": 6, "height": 8,
                          "baseLabel": "6", "heightLabel": "8" } }
  - box-plot:         Box-and-whisker plot for One-Variable Data (median,
                      quartiles, IQR, range). Params: min, q1, median, q3, max
                      (the five-number summary), axisMin, axisMax, showValues
                      (default true). Example:
                        { "type": "box-plot", "params": { "min": 2, "q1": 5,
                          "median": 7, "q3": 10, "max": 15 } }
  - dot-plot:         Dot/line plot for a small distribution (mode, median,
                      range, mean from a picture). Provide EITHER values (raw
                      numbers; repeats stack) OR data: [{ value, count }]. Plus
                      axisMin, axisMax, step. Example:
                        { "type": "dot-plot", "params": { "values": [1,2,2,3,3,3,4] } }
  - shape:            Arbitrary 2D polygon from explicit vertices — use for
                      trapezoids, parallelograms, kites, irregular quadrilaterals,
                      and composite area figures that triangle/polygon can't
                      do. Params: vertices [{ x, y, label? }] (>=3, in order; math
                      coords, auto-fit), sideLabels (string per edge), rightAngles
                      (vertex indices to mark with a right-angle square). Example
                      (a trapezoid with parallel sides 8 and 4, height 3):
                        { "type": "shape", "params": { "vertices":
                          [ {"x":0,"y":0},{"x":8,"y":0},{"x":6,"y":3},{"x":2,"y":3} ],
                          "sideLabels": ["8","","4",""], "rightAngles": [0] } }

When to use each:
  - Math/algebra/precalc questions → function-graph
  - Inequalities, intervals, set membership → number-line
  - "What's the difference between X and Y?" → comparison-table
  - Punctuation / clause-structure questions (RW boundaries) → sentence-diagram
  - "How do you decide between A and B?" rules → decision-tree
  - RW passage analysis (which sentences are evidence, etc.) → highlighted-passage
  - Right-triangle trig (sin/cos/tan from sides), Pythagorean theorem,
    special triangles (30-60-90, 45-45-90), SOHCAHTOA → right-triangle
  - Altitude to the hypotenuse / geometric mean (points A, B, C, D with the
    altitude CD; $CD^2 = AD \\cdot DB$; similar triangles inside a right
    triangle) → right-triangle-altitude (NOT right-triangle — that one has no
    point D, no altitude, and no vertex labels)
  - Unit-circle definition of sin/cos, special angles, reference angles,
    quadrant signs → unit-circle
  - Volume of a rectangular prism / box (V = ℓwh) → rectangular-prism
  - Volume of a cylinder (V = πr²h) → cylinder
  - ABSTRACT circle geometry (arcs, sectors, central/inscribed angles, chords,
    a tangent AT a point, circumference/area) → circle. NOT a coordinate-placed
    circle and NOT tangents-from-an-external-point (see CANNOT list below).
  - Any triangle (acute/obtuse/scalene/isosceles), angle-sum, exterior
    angles, similar triangles → triangle (right-only → right-triangle)
  - Parallel lines + transversal, intersecting lines, vertical angles
    → parallel-lines
  - Regular polygons, interior-angle-sum → polygon
  - Volume of a cone (V = (1/3)πr²h) → cone
  - Volume of a sphere (V = (4/3)πr³) → sphere
  - Volume of a (square) pyramid (V = (1/3)·base²·h) → pyramid
  - One-variable data: median / quartiles / IQR / range → box-plot
  - Small distributions: mode / spread / mean-from-a-picture → dot-plot

WHAT DIAGRAMS CANNOT DO (DO NOT EMBED ONE):
  - Overlaid / nested figures: similar-triangle OVERLAYS that share a
    side or nest inside each other, and any two-shapes-in-one figure. The
    triangle and polygon types each draw a SINGLE standalone shape, not
    two overlaid. (Single irregular/composite outlines — trapezoids,
    kites, L-shapes, composite area figures — ARE now supported: use the
    "shape" type with explicit vertices. Only OVERLAYS remain unsupported.)
  - Coordinate geometry beyond y = f(x): no arbitrary scattered points
    or free-form line segments, no parametric curves. (Single-curve
    shaded regions ARE supported via the function-graph "shade" param;
    a SYSTEM of two lines via "curves".)
  - A circle POSITIONED on the coordinate plane: the "circle" type is a
    generic origin-centered circle with NO axes drawn — it CANNOT show a
    circle centered at a specific point like (4,-2) with the x/y axes and
    labeled axis-intersection points (A,B,C,D). Cramming those points on it
    produces overlapping garbled labels. Explain the algebra in words + KaTeX.
  - TANGENT LINES FROM AN EXTERNAL POINT: the "circle" type's tangentAt draws
    a tangent AT a point ON the circle only — there is NO way to draw the two
    tangents from an off-circle external point. Forcing it draws a misleading
    line through the circle. Explain in words + KaTeX instead.
  - 3D shapes beyond the supported set (rectangular-prism, cylinder,
    cone, sphere, pyramid). Oblique prisms, composite/compound solids,
    and frustums are NOT supported — there's no diagram type that fits,
    so describe them in words instead of forcing a wrong type. (Note:
    cone, sphere, and square-pyramid volume formulas ARE on the SAT
    reference sheet and now have their own diagram types — use them.)
  - Rasterized images, photos, or hand-drawn sketches.

When a question is about something on the CANNOT list, DO NOT embed an
empty / wrong-type diagram (a number-line for an octagon is worse than
no diagram — it creates the illusion of a picture without one).
Skip the fence entirely and:
  - For unsupported geometry: use KaTeX to walk through the math +
    describe the shape in words.
  - For 2D coordinate questions: walk through the calculation in KaTeX.

If the JSON is malformed or the type is wrong, the diagram falls back to
a plain code block — no error to the user, just a missed opportunity.
Keep specs short; the params shapes are intentionally minimal.`

/**
 * Accuracy, said outright. Asked for "the fraction 3/5 and the exponent 2³" in
 * one formatting sample, the question tutor wrote "The fraction 3/5 equals the
 * exponent 2³ = 8" — a false statement to a student. Reproduced on its model
 * (Haiku 4.5) in 3 of 20 runs and in every run at temperature 0; this rule
 * brought it to 0 of 9 (build-7 retest, F9). Shared by question Sage and main
 * Sage.
 */
export const MATH_ACCURACY_LINE =
  "- Every mathematical statement you write must be true. Never say two quantities are equal unless they are. When the student asks you to include several unrelated items (numbers, prices, a fraction, an exponent, code), show each one on its own line or bullet — don't join unrelated values in one sentence or invent a relationship between them."

/**
 * The reference-answer block for a screen that can't draw diagrams (the iOS
 * app). The full block asks for "ONE relevant diagram", which contradicted the
 * no-diagrams rule sent alongside it.
 */
export function referenceAnswerInstructions(noDiagrams: boolean): string {
  if (!noDiagrams) return REFERENCE_ANSWER_INSTRUCTIONS
  return REFERENCE_ANSWER_INSTRUCTIONS.replace(
    /\n {2}- Add ONE relevant diagram if a supported type fits \(unit-circle for\n {4}special-angle trig, function-graph for a family of functions, etc\.\)\./,
    '',
  )
    .replace(' · Diagram ·', ' ·')
    .replace(/, a \\?`\\?`\\?`diagram unit-circle block/, '')
}
